import {
  Badge,
  BrailleSpinner,
  Button,
  Dialog,
  DialogBackdrop,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Skeleton,
  Switch,
} from "@autonoma/blacklight";
import { ArrowCounterClockwiseIcon } from "@phosphor-icons/react/ArrowCounterClockwise";
import { BuildingsIcon } from "@phosphor-icons/react/Buildings";
import { CalendarBlankIcon } from "@phosphor-icons/react/CalendarBlank";
import { CheckIcon } from "@phosphor-icons/react/Check";
import { CheckCircleIcon } from "@phosphor-icons/react/CheckCircle";
import { ClockIcon } from "@phosphor-icons/react/Clock";
import { CubeTransparentIcon } from "@phosphor-icons/react/CubeTransparent";
import { CurrencyDollarIcon } from "@phosphor-icons/react/CurrencyDollar";
import { DownloadSimpleIcon } from "@phosphor-icons/react/DownloadSimple";
import { GiftIcon } from "@phosphor-icons/react/Gift";
import { GithubLogoIcon } from "@phosphor-icons/react/GithubLogo";
import { GlobeIcon } from "@phosphor-icons/react/Globe";
import { InfinityIcon } from "@phosphor-icons/react/Infinity";
import { PlusIcon } from "@phosphor-icons/react/Plus";
import { UsersIcon } from "@phosphor-icons/react/Users";
import { WarningCircleIcon } from "@phosphor-icons/react/WarningCircle";
import { XIcon } from "@phosphor-icons/react/X";
import { QueryErrorResetBoundary } from "@tanstack/react-query";
import { CatchBoundary, Link, Navigate, createFileRoute, useRouteContext, useRouter } from "@tanstack/react-router";
import { useAuth } from "lib/auth";
import { formatDate } from "lib/format";
import {
  type AdminOrganizationsInput,
  type AdminPromoCodesInput,
  useAdminGitHubRepositories,
  useAdminOrganizations,
  useAdminPendingOrgs,
  useApproveOrg,
  useAdminPromoCodes,
  useCreatePromoCodeAdmin,
  useCreateOrg,
  useDownloadAdminGitHubRepository,
  useRejectOrg,
  useSetPromoCodeActiveAdmin,
  useSwitchToOrg,
  useUpdateUnlimitedCreditsAdmin,
} from "lib/query/admin.queries";
import { Suspense, useEffect, useState } from "react";
import { clearLastAppId } from "../-last-app";

export const Route = createFileRoute("/_blacklight/_app-shell/admin/")({
  component: AdminPage,
});

type Tab = "organizations" | "pending" | "promoCodes" | "githubRepos";
const ORGANIZATIONS_PER_PAGE = 20;
const ORGANIZATION_SEARCH_DEBOUNCE_MS = 300;

// ─── Guard ────────────────────────────────────────────────────────────────────

function AdminGuard({ children }: { children: React.ReactNode }) {
  const { isAdmin } = useAuth();

  if (!isAdmin) {
    return <Navigate to="/" />;
  }

  return <>{children}</>;
}

// ─── Org row ──────────────────────────────────────────────────────────────────

interface OrgRowProps {
  org: {
    id: string;
    name: string;
    slug: string;
    domain?: string;
    createdAt: Date;
    memberCount: number;
    applicationCount: number;
    unlimitedCredits: boolean;
  };
  activeOrgId: string | undefined;
  onActivate: (orgId: string) => void;
  isActivating: boolean;
  onToggleUnlimitedCredits: (orgId: string, unlimitedCredits: boolean) => void;
  isTogglingUnlimitedCredits: boolean;
}

function OrgRow({
  org,
  activeOrgId,
  onActivate,
  isActivating,
  onToggleUnlimitedCredits,
  isTogglingUnlimitedCredits,
}: OrgRowProps) {
  const isActive = activeOrgId === org.id;

  return (
    <div
      className={`flex items-center gap-4 rounded-md border px-4 py-3 transition-colors ${
        isActive
          ? "border-primary-ink/30 bg-primary-ink/5"
          : "border-border-dim bg-surface-base hover:bg-surface-raised"
      }`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-medium text-text-primary">{org.name}</p>
          {isActive && (
            <Badge variant="outline" className="text-3xs">
              Active
            </Badge>
          )}
          {org.unlimitedCredits && (
            <Badge variant="success" className="text-3xs">
              <InfinityIcon size={11} weight="bold" />
              Unlimited credits
            </Badge>
          )}
        </div>
        <div className="mt-0.5 flex items-center gap-2 font-mono text-2xs text-text-secondary">
          <span>{org.slug}</span>
          {org.domain != null && (
            <span className="flex items-center gap-1">
              <GlobeIcon size={12} />
              {org.domain}
            </span>
          )}
        </div>
      </div>

      <div className="hidden items-center gap-6 shrink-0 sm:flex">
        <div className="flex items-center gap-1.5 text-2xs text-text-tertiary">
          <UsersIcon size={14} />
          <span>
            {org.memberCount} {org.memberCount === 1 ? "member" : "members"}
          </span>
        </div>
        <div className="flex items-center gap-1.5 text-2xs text-text-tertiary">
          <CubeTransparentIcon size={14} />
          <span>
            {org.applicationCount} {org.applicationCount === 1 ? "app" : "apps"}
          </span>
        </div>
        <div className="flex items-center gap-1.5 text-2xs text-text-tertiary shrink-0">
          <CalendarBlankIcon size={14} />
          <span>{formatDate(org.createdAt)}</span>
        </div>
      </div>

      <div
        className="hidden shrink-0 items-center gap-2 sm:flex"
        title="Unlimited credits: every gate passes and the balance never moves, but usage is still recorded"
      >
        <InfinityIcon size={14} className={org.unlimitedCredits ? "text-status-success" : "text-text-secondary"} />
        <Switch
          checked={org.unlimitedCredits}
          disabled={isTogglingUnlimitedCredits}
          onCheckedChange={(checked) => onToggleUnlimitedCredits(org.id, checked)}
          aria-label={`Unlimited credits for ${org.name}`}
        />
      </div>

      <div className="shrink-0 w-20 flex justify-end">
        {isActive ? (
          <span className="text-2xs font-medium text-primary-ink">Current</span>
        ) : (
          <Button variant="outline" size="sm" disabled={isActivating} onClick={() => onActivate(org.id)}>
            {isActivating ? <BrailleSpinner animation="braille" size="sm" /> : "Switch"}
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Org list ─────────────────────────────────────────────────────────────────

function OrgList() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showIndividualUsers, setShowIndividualUsers] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, ORGANIZATION_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const listInput: AdminOrganizationsInput = {
    page,
    pageSize: ORGANIZATIONS_PER_PAGE,
    query: debouncedSearch.trim() === "" ? undefined : debouncedSearch.trim(),
    organizationType: showIndividualUsers ? "individual" : "company",
  };
  const resultsResetKey = `${listInput.organizationType}:${listInput.page}:${listInput.query ?? ""}`;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Input
          placeholder="Search organizations..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="sm:flex-1"
        />
        <div className="flex shrink-0 items-center justify-center gap-2 rounded-md border border-border-dim bg-surface-base px-3 py-2">
          <span className={`text-2xs ${showIndividualUsers ? "text-text-secondary" : "font-medium text-text-primary"}`}>
            Company domains
          </span>
          <Switch
            checked={showIndividualUsers}
            onCheckedChange={(checked) => {
              setShowIndividualUsers(checked);
              setPage(1);
            }}
            aria-label="Show individual user organizations"
          />
          <span className={`text-2xs ${showIndividualUsers ? "font-medium text-text-primary" : "text-text-secondary"}`}>
            Individual users
          </span>
        </div>
      </div>
      <QueryErrorResetBoundary>
        {({ reset }) => (
          <CatchBoundary
            getResetKey={() => resultsResetKey}
            errorComponent={({ reset: resetBoundary }) => (
              <OrganizationResultsError
                onRetry={() => {
                  reset();
                  resetBoundary();
                }}
              />
            )}
          >
            <Suspense fallback={<OrgListSkeleton />}>
              <OrganizationResults input={listInput} onPageChange={setPage} />
            </Suspense>
          </CatchBoundary>
        )}
      </QueryErrorResetBoundary>
    </div>
  );
}

function OrganizationResults({
  input,
  onPageChange,
}: {
  input: AdminOrganizationsInput;
  onPageChange: (page: number) => void;
}) {
  const activeOrg = useRouteContext({
    from: "/_blacklight/_app-shell",
    select: (ctx) => ctx.activeOrganization,
  });
  const activeOrgId = activeOrg?.id;
  const [activatingId, setActivatingId] = useState<string | undefined>();
  const [togglingCreditsId, setTogglingCreditsId] = useState<string | undefined>();
  const router = useRouter();
  const { data } = useAdminOrganizations(input);
  const switchMutation = useSwitchToOrg();
  const unlimitedCreditsMutation = useUpdateUnlimitedCreditsAdmin();

  function handleToggleUnlimitedCredits(organizationId: string, unlimitedCredits: boolean) {
    setTogglingCreditsId(organizationId);
    unlimitedCreditsMutation.mutate(
      { organizationId, unlimitedCredits },
      { onSettled: () => setTogglingCreditsId(undefined) },
    );
  }

  function handleActivate(orgId: string) {
    setActivatingId(orgId);
    switchMutation.mutate(
      { orgId },
      {
        onSuccess: () => {
          // The last-viewed app belongs to the previous org - drop it so the new org lands on its own first app.
          clearLastAppId();
          void router.navigate({ to: "/", reloadDocument: true });
        },
        onSettled: () => setActivatingId(undefined),
      },
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2">
        {data.items.map((org) => (
          <OrgRow
            key={org.id}
            org={org}
            activeOrgId={activeOrgId}
            onActivate={(id) => handleActivate(id)}
            isActivating={activatingId === org.id}
            onToggleUnlimitedCredits={handleToggleUnlimitedCredits}
            isTogglingUnlimitedCredits={togglingCreditsId === org.id}
          />
        ))}
        {data.items.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border-dim py-14 text-center">
            <BuildingsIcon size={24} className="text-text-secondary" />
            <p className="text-sm text-text-secondary">
              {input.query != null
                ? `No organizations matching "${input.query}"`
                : input.organizationType === "individual"
                  ? "No individual user organizations found"
                  : "No company domain organizations found"}
            </p>
          </div>
        )}
      </div>
      {data.items.length > 0 && (
        <div className="flex items-center justify-between gap-3 pt-1">
          <p className="text-2xs text-text-secondary">
            Page {data.page} of {data.totalPages} · {data.total} organizations
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(Math.max(1, data.page - 1))}
              disabled={data.page === 1}
              aria-label="Previous organizations page"
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(Math.min(data.totalPages, data.page + 1))}
              disabled={data.page === data.totalPages}
              aria-label="Next organizations page"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function OrgListSkeleton() {
  return (
    <div className="flex flex-col gap-2">
      {[1, 2, 3].map((i) => (
        <Skeleton key={i} className="h-16 w-full rounded-md" />
      ))}
    </div>
  );
}

function OrganizationResultsError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-md border border-status-critical/30 bg-status-critical/5 py-14 text-center">
      <WarningCircleIcon size={24} weight="duotone" className="text-status-critical" />
      <div className="space-y-1">
        <p className="text-sm font-medium text-text-primary">Couldn't load organizations</p>
        <p className="text-2xs text-text-secondary">Check your connection and try again.</p>
      </div>
      <Button variant="outline" size="sm" onClick={onRetry}>
        <ArrowCounterClockwiseIcon size={14} />
        Retry
      </Button>
    </div>
  );
}

function PromoCodesAdmin() {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <PromoCodeCreateCard />
      <Suspense fallback={<PromoCodeListSkeleton />}>
        <PromoCodeListCard />
      </Suspense>
    </div>
  );
}

function PromoCodeCreateCard() {
  const [code, setCode] = useState("");
  const [credits, setCredits] = useState("100000");
  const [maxRedemptions, setMaxRedemptions] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const createPromoCode = useCreatePromoCodeAdmin();

  function handleCreatePromoCode() {
    const normalizedCode = code.trim();
    const grantCredits = Number.parseInt(credits, 10);
    const max = maxRedemptions.trim() === "" ? null : Number.parseInt(maxRedemptions, 10);
    const expiration = endsAt.trim() === "" ? null : new Date(endsAt);
    if (normalizedCode.length === 0 || !Number.isFinite(grantCredits) || grantCredits <= 0) return;
    if (max != null && (!Number.isFinite(max) || max <= 0)) return;
    if (expiration != null && Number.isNaN(expiration.getTime())) return;

    createPromoCode.mutate(
      {
        code: normalizedCode,
        grantCredits,
        maxRedemptions: max,
        endsAt: expiration,
      },
      {
        onSuccess: () => {
          setCode("");
          setCredits("100000");
          setMaxRedemptions("");
          setEndsAt("");
        },
      },
    );
  }

  return (
    <div className="rounded-md border border-border-dim bg-surface-base p-4">
      <h2 className="text-sm font-medium text-text-primary">Create promo code</h2>
      <div className="mt-3 space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="admin-promo-code">Name / code</Label>
          <Input
            id="admin-promo-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="0BUGS"
            aria-label="admin-promo-code-name"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="admin-promo-credits">Credits</Label>
          <Input
            id="admin-promo-credits"
            type="number"
            min={1}
            value={credits}
            onChange={(e) => setCredits(e.target.value)}
            aria-label="admin-promo-code-credits"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="admin-promo-max-redemptions">Max redemptions (optional)</Label>
          <Input
            id="admin-promo-max-redemptions"
            type="number"
            min={1}
            value={maxRedemptions}
            onChange={(e) => setMaxRedemptions(e.target.value)}
            aria-label="admin-promo-code-max-redemptions"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="admin-promo-expiration">Expires at (optional)</Label>
          <Input
            id="admin-promo-expiration"
            type="datetime-local"
            value={endsAt}
            onChange={(e) => setEndsAt(e.target.value)}
            aria-label="admin-promo-code-expires-at"
          />
          <p className="text-2xs text-text-tertiary">Timezone: local browser time (stored in UTC).</p>
        </div>
        <Button
          onClick={handleCreatePromoCode}
          disabled={code.trim().length === 0 || createPromoCode.isPending}
          aria-label="admin-promo-code-create"
        >
          {createPromoCode.isPending ? "Creating..." : "Create promo code"}
        </Button>
      </div>
    </div>
  );
}

function PromoCodeListCard() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"all" | "active" | "inactive">("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const listInput: AdminPromoCodesInput = {
    page,
    pageSize,
    query: search.trim() === "" ? undefined : search.trim(),
    isActive: status === "all" ? undefined : status === "active",
  };
  const { data } = useAdminPromoCodes(listInput);
  const setPromoCodeActive = useSetPromoCodeActiveAdmin();

  return (
    <div className="rounded-md border border-border-dim bg-surface-base p-4">
      <h2 className="text-sm font-medium text-text-primary">Manage promo codes</h2>
      <div className="mt-3 space-y-3">
        <div className="grid items-center gap-2 sm:grid-cols-[minmax(0,1fr)_180px_140px]">
          <Input
            placeholder="Search code or description..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="h-9"
            aria-label="admin-promo-code-search"
          />
          <select
            className="h-9 rounded-md border border-border-dim bg-surface-base px-3 text-sm text-text-primary"
            value={status}
            onChange={(e) => {
              const value = e.target.value as "all" | "active" | "inactive";
              setStatus(value);
              setPage(1);
            }}
            aria-label="admin-promo-code-status-filter"
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <select
            className="h-9 rounded-md border border-border-dim bg-surface-base px-3 text-sm text-text-primary"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number.parseInt(e.target.value, 10));
              setPage(1);
            }}
            aria-label="admin-promo-code-page-size"
          >
            <option value={10}>10 / page</option>
            <option value={20}>20 / page</option>
            <option value={50}>50 / page</option>
          </select>
        </div>

        {data.items.length === 0 ? (
          <p className="text-sm text-text-tertiary">No promo codes yet.</p>
        ) : (
          data.items.map((promo) => {
            const remaining =
              promo.maxRedemptions == null ? "∞" : Math.max(0, promo.maxRedemptions - promo.redeemedCount);

            return (
              <div key={promo.id} className="rounded-md border border-border-dim px-3 py-2">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-mono text-2xs text-text-primary">{promo.code}</p>
                    <p className="font-mono text-3xs text-text-tertiary">
                      +{promo.grantCredits.toLocaleString()} credits · Remaining: {remaining}
                    </p>
                    <p className="font-mono text-3xs text-text-tertiary">
                      {promo.endsAt == null
                        ? "No expiration"
                        : `Expires ${new Date(promo.endsAt).toLocaleString()} (local)`}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() =>
                      setPromoCodeActive.mutate({
                        promoCodeId: promo.id,
                        isActive: !promo.isActive,
                      })
                    }
                    disabled={setPromoCodeActive.isPending}
                    aria-label={`admin-promo-code-toggle-${promo.code.toLowerCase()}`}
                  >
                    {promo.isActive ? "Disable" : "Enable"}
                  </Button>
                </div>
              </div>
            );
          })
        )}

        <div className="flex items-center justify-between pt-1">
          <p className="text-2xs text-text-tertiary">
            Page {data.page} of {data.totalPages} · {data.total} total
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={data.page <= 1}
              aria-label="admin-promo-code-prev-page"
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
              disabled={data.page >= data.totalPages}
              aria-label="admin-promo-code-next-page"
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PromoCodeListSkeleton() {
  return <Skeleton className="h-[380px] w-full rounded-md" />;
}

// ─── GitHub repositories ─────────────────────────────────────────────────────

function GitHubRepositoriesAdmin() {
  const [search, setSearch] = useState("");
  const [downloadingKey, setDownloadingKey] = useState<string | undefined>();
  const { data: repos } = useAdminGitHubRepositories();
  const downloadRepository = useDownloadAdminGitHubRepository();

  const q = search.toLowerCase().trim();
  const filteredRepos =
    q === ""
      ? repos
      : repos.filter((repo) =>
          [
            repo.name,
            repo.repositoryName,
            repo.id.toString(),
            repo.installationId.toString(),
            repo.installationAccountLogin,
          ].some((value) => value.toLowerCase().includes(q)),
        );

  function handleDownload(repo: (typeof repos)[number]) {
    const key = `${repo.installationId}:${repo.id}`;
    setDownloadingKey(key);
    downloadRepository.mutate(
      { installationId: repo.installationId, repositoryId: repo.id },
      {
        onSuccess: ({ downloadUrl, fileName }) => {
          const anchor = document.createElement("a");
          anchor.href = downloadUrl;
          anchor.download = fileName;
          anchor.rel = "noopener noreferrer";
          document.body.append(anchor);
          anchor.click();
          anchor.remove();
        },
        onSettled: () => setDownloadingKey(undefined),
      },
    );
  }

  return (
    <div className="rounded-md border border-border-dim bg-surface-base">
      <div className="flex flex-col gap-3 border-b border-border-dim p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-sm font-medium text-text-primary">GitHub App repositories</h2>
          <p className="mt-0.5 text-2xs text-text-tertiary">{repos.length} repositories across installations</p>
        </div>
        <Input
          placeholder="Filter repositories..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-9 sm:max-w-xs"
          aria-label="admin-github-repositories-filter"
        />
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-border-dim bg-surface-raised/50">
            <tr>
              <th className="px-4 py-2 text-2xs font-medium uppercase text-text-tertiary">Name</th>
              <th className="px-4 py-2 text-2xs font-medium uppercase text-text-tertiary">Installation ID</th>
              <th className="px-4 py-2 text-2xs font-medium uppercase text-text-tertiary">Repository ID</th>
              <th className="px-4 py-2 text-right text-2xs font-medium uppercase text-text-tertiary">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-dim">
            {filteredRepos.map((repo) => {
              const rowKey = `${repo.installationId}:${repo.id}`;
              const isDownloading = downloadRepository.isPending && downloadingKey === rowKey;

              return (
                <tr key={rowKey} className="hover:bg-surface-raised/50">
                  <td className="max-w-[360px] px-4 py-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <GithubLogoIcon size={16} className="shrink-0 text-text-tertiary" />
                      <div className="min-w-0">
                        <p className="truncate font-medium text-text-primary">{repo.name}</p>
                        <p className="truncate font-mono text-3xs text-text-tertiary">
                          {repo.installationAccountLogin} · {repo.installationAccountType}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-2xs text-text-secondary">{repo.installationId}</td>
                  <td className="px-4 py-3 font-mono text-2xs text-text-secondary">{repo.id}</td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={downloadRepository.isPending}
                      onClick={() => handleDownload(repo)}
                      aria-label={`download-${repo.name}`}
                    >
                      {isDownloading ? (
                        <BrailleSpinner animation="braille" size="sm" />
                      ) : (
                        <DownloadSimpleIcon size={14} />
                      )}
                      Download
                    </Button>
                  </td>
                </tr>
              );
            })}

            {filteredRepos.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-14 text-center">
                  <div className="flex flex-col items-center justify-center gap-2 text-text-tertiary">
                    <GithubLogoIcon size={24} />
                    <p className="text-sm">
                      {search !== "" ? `No repositories matching "${search}"` : "No repositories found"}
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GitHubRepositoriesSkeleton() {
  return <Skeleton className="h-[420px] w-full rounded-md" />;
}

// ─── Pending Approvals ────────────────────────────────────────────────────────

interface PendingOrgRowProps {
  org: {
    id: string;
    name: string;
    slug: string;
    domain: string | null;
    createdAt: Date;
    memberCount: number;
  };
  onApprove: (orgId: string) => void;
  onReject: (orgId: string) => void;
  isBusy: boolean;
}

function PendingOrgRow({ org, onApprove, onReject, isBusy }: PendingOrgRowProps) {
  return (
    <div className="flex items-center gap-4 rounded-md border border-status-warn/30 bg-status-warn/5 px-4 py-3 transition-colors">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-medium text-text-primary">{org.name}</p>
          <Badge variant="outline" className="text-3xs text-status-warn border-status-warn/30">
            Pending
          </Badge>
        </div>
        <div className="mt-0.5 flex items-center gap-2">
          <p className="font-mono text-2xs text-text-tertiary">{org.slug}</p>
          {org.domain != null && (
            <div className="flex items-center gap-1 text-2xs text-text-tertiary">
              <GlobeIcon size={12} />
              <span>{org.domain}</span>
            </div>
          )}
        </div>
      </div>

      <div className="hidden items-center gap-6 shrink-0 sm:flex">
        <div className="flex items-center gap-1.5 text-2xs text-text-tertiary">
          <UsersIcon size={14} />
          <span>
            {org.memberCount} {org.memberCount === 1 ? "member" : "members"}
          </span>
        </div>
        <div className="flex items-center gap-1.5 text-2xs text-text-tertiary shrink-0">
          <CalendarBlankIcon size={14} />
          <span>{formatDate(org.createdAt)}</span>
        </div>
      </div>

      <div className="flex shrink-0 gap-2">
        <Button variant="outline" size="sm" disabled={isBusy} onClick={() => onReject(org.id)}>
          <XIcon size={14} />
          Reject
        </Button>
        <Button size="sm" disabled={isBusy} onClick={() => onApprove(org.id)}>
          <CheckIcon size={14} />
          Approve
        </Button>
      </div>
    </div>
  );
}

function PendingOrgList() {
  const [busyId, setBusyId] = useState<string | undefined>();

  const { data: pendingOrgs } = useAdminPendingOrgs();
  const approveMutation = useApproveOrg();
  const rejectMutation = useRejectOrg();

  function handleApprove(orgId: string) {
    setBusyId(orgId);
    approveMutation.mutate({ orgId }, { onSuccess: () => setBusyId(undefined) });
  }

  function handleReject(orgId: string) {
    setBusyId(orgId);
    rejectMutation.mutate({ orgId }, { onSuccess: () => setBusyId(undefined) });
  }

  return (
    <div className="flex flex-col gap-2">
      {pendingOrgs.map((org) => (
        <PendingOrgRow
          key={org.id}
          org={org}
          onApprove={handleApprove}
          onReject={handleReject}
          isBusy={busyId === org.id}
        />
      ))}
      {pendingOrgs.length === 0 && (
        <div className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border-dim py-14 text-center">
          <CheckCircleIcon size={24} className="text-text-tertiary" />
          <p className="text-sm text-text-tertiary">No pending approvals</p>
        </div>
      )}
    </div>
  );
}

// ─── Create Organization ──────────────────────────────────────────────────────

function CreateOrgDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");

  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

  const createMutation = useCreateOrg();

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (name.trim() === "" || domain.trim() === "") return;
    createMutation.mutate(
      { name: name.trim(), slug, domain: domain.trim() },
      {
        onSuccess: () => {
          onOpenChange(false);
          setName("");
          setDomain("");
        },
      },
    );
  }

  const isValid = name.trim() !== "" && domain.trim() !== "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogBackdrop />
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create organization</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 px-6 py-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="org-name">Name</Label>
            <Input
              id="org-name"
              placeholder="Acme Inc."
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
            {slug !== "" && <p className="font-mono text-2xs text-text-tertiary">Slug: {slug}</p>}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="org-domain">Domain</Label>
            <Input id="org-domain" placeholder="acme.com" value={domain} onChange={(e) => setDomain(e.target.value)} />
          </div>
          {createMutation.error != null && (
            <p className="text-sm text-status-critical">{createMutation.error.message}</p>
          )}
        </form>
        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>Cancel</DialogClose>
          <Button
            onClick={() => {
              if (isValid) {
                createMutation.mutate(
                  { name: name.trim(), slug, domain: domain.trim() },
                  {
                    onSuccess: () => {
                      onOpenChange(false);
                      setName("");
                      setDomain("");
                    },
                  },
                );
              }
            }}
            disabled={!isValid || createMutation.isPending}
          >
            {createMutation.isPending ? "Creating..." : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function AdminContent() {
  const [tab, setTab] = useState<Tab>("organizations");
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <section className="flex-1 overflow-auto">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-xl font-medium text-text-primary">Admin</h1>
            </div>
            <p className="mt-1 text-xs text-text-secondary">
              Manage organizations, approve access requests, and debug accounts.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 sm:justify-end">
            <Button variant="outline" size="sm" render={<Link to="/admin/previewkit" />}>
              <CubeTransparentIcon size={14} />
              Preview environments
            </Button>
            <Button variant="outline" size="sm" render={<Link to="/admin/compute-billing" />}>
              <CurrencyDollarIcon size={14} />
              Compute billing
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCreateOpen(true)}>
              <PlusIcon size={14} />
              Create organization
            </Button>
            <div className="flex max-w-full overflow-x-auto rounded-md border border-border-dim">
              <button
                type="button"
                onClick={() => setTab("organizations")}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-2xs font-medium transition-colors rounded-l-md ${
                  tab === "organizations"
                    ? "bg-surface-raised text-text-primary"
                    : "text-text-tertiary hover:text-text-primary"
                }`}
              >
                <BuildingsIcon size={14} />
                Organizations
              </button>
              <button
                type="button"
                onClick={() => setTab("pending")}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-2xs font-medium transition-colors ${
                  tab === "pending"
                    ? "bg-surface-raised text-text-primary"
                    : "text-text-tertiary hover:text-text-primary"
                }`}
              >
                <ClockIcon size={14} />
                Pending
              </button>
              <button
                type="button"
                onClick={() => setTab("promoCodes")}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-2xs font-medium transition-colors ${
                  tab === "promoCodes"
                    ? "bg-surface-raised text-text-primary"
                    : "text-text-tertiary hover:text-text-primary"
                }`}
              >
                <GiftIcon size={14} />
                Promo codes
              </button>
              <button
                type="button"
                onClick={() => setTab("githubRepos")}
                className={`flex items-center gap-1.5 rounded-r-md px-3 py-1.5 text-2xs font-medium transition-colors ${
                  tab === "githubRepos"
                    ? "bg-surface-raised text-text-primary"
                    : "text-text-tertiary hover:text-text-primary"
                }`}
              >
                <GithubLogoIcon size={14} />
                GitHub repos
              </button>
            </div>
          </div>
        </div>

        {tab === "organizations" && <OrgList />}

        {tab === "pending" && (
          <Suspense fallback={<OrgListSkeleton />}>
            <PendingOrgList />
          </Suspense>
        )}

        {tab === "promoCodes" && (
          <Suspense fallback={<OrgListSkeleton />}>
            <PromoCodesAdmin />
          </Suspense>
        )}

        {tab === "githubRepos" && (
          <Suspense fallback={<GitHubRepositoriesSkeleton />}>
            <GitHubRepositoriesAdmin />
          </Suspense>
        )}
      </div>

      <CreateOrgDialog open={createOpen} onOpenChange={setCreateOpen} />
    </section>
  );
}

function AdminPage() {
  return (
    <AdminGuard>
      <AdminContent />
    </AdminGuard>
  );
}
